import {
    Checkbox,
    IconButton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TableTypeMap,
    Theme,
} from "@mui/material";
import {useStyles} from "@/components/call-group/members/styles";
import {UpdateIvrProfileMutationVariables} from "./mutation.generated";
import {DragHandleIcon} from "@/components/widgets";
import {moveItemsInArray, removeFileExtension} from "@/tools";
import {FormattedMessage, defineMessage} from "react-intl";
import {useEffect, useState} from "react";
import {FormattedTooltip, FormattedButton, ErrorWithContext} from "@dvrt/graphql-widgets";
import ThemedFontAwesomeIcon from "@/components/ThemedFontAwesomeIcon";
import {makeStyles} from "tss-react/mui";
import {SortableContext, useSortable, verticalListSortingStrategy} from "@dnd-kit/sortable";
import {CSS} from "@dnd-kit/utilities";
import {DndContext, DragEndEvent} from "@dnd-kit/core";
import {restrictToParentElement, restrictToVerticalAxis} from "@dnd-kit/modifiers";
import DomainMediaSelector from "@/components/DomainMediaSelector";
import {MediaDownloadToken} from "./tools";

const createEmptyMedia = (): UpdateIvrMenuMedia => ({
    id: undefined,
    file: undefined,
    description: undefined,
    disabled: false,
    downloadToken: undefined,
});

const useErrorStyles = makeStyles()((theme: Theme) => ({
    left: {
        border: `2px solid ${theme.palette.error.main}`,
        borderStyle: "solid none solid solid",
        borderRadius: "10px 0 0 10px",
    },
    right: {
        border: `2px solid ${theme.palette.error.main}`,
        borderStyle: "solid solid solid none",
        borderRadius: "0 10px 10px 0",
    },
    center: {
        border: `2px solid ${theme.palette.error.main}`,
        borderStyle: "solid none solid none",
    },
    disabled: {
        backgroundColor: theme.palette.action.disabledBackground,
    },
    base: {
        transition: "all 0.2s ease-in-out",
    },
}));

export type UpdateIvrMenuMedia = UpdateIvrProfileMutationVariables["greetings"][number];

const FileToMenuMedia = (file: File): UpdateIvrMenuMedia => ({
    description: removeFileExtension(file.name),
    file: file,
    downloadToken: undefined,
    id: undefined,
});

type BaseProps = {
    domainId: number;
    disabled?: boolean;
};

type MediaRowProps = {
    sortId: number;
    item: UpdateIvrMenuMedia;
    openPlayerDialog: (token: MediaDownloadToken) => void;
    update: (item: UpdateIvrMenuMedia) => void;
    toggleMedia: (disabled: boolean) => void;
    deleteGreeting: () => void;
    errors?: ErrorWithContext[];
} & BaseProps;

const MediaRow = ({
    domainId,
    sortId,
    item,
    openPlayerDialog,
    update,
    toggleMedia,
    deleteGreeting,
    disabled,
    errors,
}: MediaRowProps) => {
    const {classes} = useStyles();
    const {attributes, listeners, setNodeRef, transform, transition, setActivatorNodeRef} = useSortable({
        id: sortId,
    });

    const {classes: cellClasses} = useErrorStyles();

    const draggableStyle = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    const getClassNames = (): string => {
        let result = cellClasses.base;

        const append = (className: string) => {
            result += ` ${className}`;
        };

        if (item.disabled) {
            append(cellClasses.disabled);
        }

        return result;
    };

    return (
        <TableRow className={classes.sectionRow} ref={setNodeRef} style={draggableStyle}>
            <TableCell className={getClassNames()}>
                <Stack direction="row" alignItems="center" spacing={2}>
                    <DragHandleIcon
                        disabled={disabled}
                        setActivatorNodeRef={setActivatorNodeRef}
                        attributes={attributes}
                        listeners={listeners}
                    />
                    <FormattedTooltip
                        title={defineMessage({
                            id: "ivr.tooltips.toggle",
                            defaultMessage: "Toggle whether the media is enabled or not (uncheck to disable it)",
                        })}
                    >
                        <Checkbox checked={!item.disabled} onChange={(_, checked) => toggleMedia(!checked)} />
                    </FormattedTooltip>
                </Stack>
            </TableCell>
            <TableCell className={getClassNames()}>
                <DomainMediaSelector
                    domainId={domainId}
                    value={item.id ?? item.file}
                    onChange={(value) => {
                        if (value === undefined) return;
                        if (value instanceof File) {
                            update(FileToMenuMedia(value));
                        } else {
                            update({
                                id: value.id,
                                description: value.description,
                                downloadToken: undefined,
                                file: undefined,
                            });
                        }
                    }}
                    errors={errors}
                />
            </TableCell>
            <TableCell className={getClassNames()}>
                <Stack direction="row" spacing={2} alignItems="center">
                    {item.downloadToken && (
                        <FormattedTooltip
                            title={defineMessage({id: "ivr.tooltips.play", defaultMessage: "Play media"})}
                        >
                            <IconButton onClick={() => openPlayerDialog(item.downloadToken)} disabled={disabled}>
                                <ThemedFontAwesomeIcon icon="play" color="primary" />
                            </IconButton>
                        </FormattedTooltip>
                    )}
                    <FormattedTooltip
                        title={defineMessage({id: "ivr.tooltips.delete", defaultMessage: "Delete media"})}
                    >
                        <IconButton onClick={() => deleteGreeting()} disabled={disabled}>
                            <ThemedFontAwesomeIcon icon="times" color="primary" />
                        </IconButton>
                    </FormattedTooltip>
                </Stack>
            </TableCell>
        </TableRow>
    );
};

type MediaListProps = {
    items: UpdateIvrMenuMedia[];
    openPlayerDialog: (token: MediaDownloadToken) => void;
    update: (index: number, item: UpdateIvrMenuMedia) => void;
    toggleMedia: (index: number, disabled: boolean) => void;
    deleteGreeting: (index: number) => void;
    onOrderChanged: (items: UpdateIvrMenuMedia[]) => void;
    getErrorsForIndex: (index: number) => ErrorWithContext[];
} & BaseProps;

const MediaListWithItems = ({
    domainId,
    items,
    openPlayerDialog,
    update,
    toggleMedia,
    deleteGreeting,
    onOrderChanged,
    disabled,
    getErrorsForIndex,
}: MediaListProps) => {
    // returning +1 to avoid 0 index, because it can be buggy sometimes (maybe because it is falsy?)
    const getIdArray = (length: number) => Array.from({length}, (_, i) => i + 1);

    const [sortIds, setSortIds] = useState<number[]>(() => getIdArray(items.length));

    useEffect(() => {
        setSortIds(getIdArray(items.length));
    }, [items.length]);

    const handleDragEnd = (event: DragEndEvent) => {
        const {active, over} = event;

        if (active.id !== over?.id) {
            const oldIndex = sortIds.indexOf(active.id as number);
            const newIndex = sortIds.indexOf(over.id as number);

            const newItems = moveItemsInArray(items, oldIndex, newIndex);
            onOrderChanged(newItems);
            setSortIds(moveItemsInArray(sortIds, oldIndex, newIndex));
        }
    };

    return (
        <DndContext onDragEnd={handleDragEnd} modifiers={[restrictToVerticalAxis, restrictToParentElement]}>
            <SortableContext items={sortIds} strategy={verticalListSortingStrategy}>
                <TableBody>
                    {items.map((item, index) => (
                        <MediaRow
                            domainId={domainId}
                            key={sortIds[index]}
                            sortId={sortIds[index]}
                            item={item}
                            openPlayerDialog={openPlayerDialog}
                            update={(item) => update(index, item)}
                            toggleMedia={(disabled) => toggleMedia(index, disabled)}
                            deleteGreeting={() => deleteGreeting(index)}
                            disabled={disabled}
                            errors={getErrorsForIndex(index)}
                        />
                    ))}
                </TableBody>
            </SortableContext>
        </DndContext>
    );
};

const NoMedia = ({update, disabled}: {update: (greetings: UpdateIvrMenuMedia[]) => void; disabled: boolean}) => (
    <FormattedButton
        message={defineMessage({id: "add", defaultMessage: "Add"})}
        onClick={() => update([createEmptyMedia()])}
        disabled={disabled}
    />
);

type MediaTableProps = {
    /** List of menu media. */
    media: UpdateIvrMenuMedia[];

    openMediaPlayerDialog: (token: MediaDownloadToken) => void;

    /** Callback to update menu media. */
    update: (greetings: UpdateIvrMenuMedia[]) => void;

    /** Additional MUI styling for the table */
    tableStyle?: TableTypeMap["props"]["sx"];

    getErrorsForIndex: (index: number) => ErrorWithContext[];
} & BaseProps;

/**
 * Component to display menu media for ivr profile.
 * */
const MediaTableWithItems = ({
    domainId,
    media,
    openMediaPlayerDialog,
    update,
    tableStyle,
    getErrorsForIndex,
}: MediaTableProps) => {
    return (
        <Table sx={{borderCollapse: "separate", ...tableStyle}}>
            <TableHead>
                <TableRow>
                    <TableCell></TableCell>
                    <TableCell>
                        <FormattedMessage id="description" defaultMessage="Description" />
                    </TableCell>
                    <TableCell>
                        <FormattedButton
                            message={defineMessage({id: "add", defaultMessage: "Add"})}
                            onClick={() => update([...media, createEmptyMedia()])}
                        />
                    </TableCell>
                </TableRow>
            </TableHead>
            <MediaListWithItems
                domainId={domainId}
                openPlayerDialog={openMediaPlayerDialog}
                update={(index, item) => update(media.map((m, i) => (i === index ? {...m, ...item} : m)))}
                toggleMedia={(index, disabled) =>
                    update(media.map((item, i) => (i === index ? {...item, disabled} : item)))
                }
                onOrderChanged={(items) => update(items)}
                deleteGreeting={(index) => update(media.filter((_, i) => i !== index))}
                items={media}
                getErrorsForIndex={getErrorsForIndex}
            />
        </Table>
    );
};

const MediaTable = ({
    domainId,
    disabled,
    openMediaPlayerDialog,
    media,
    update,
    tableStyle,
    getErrorsForIndex,
}: MediaTableProps) => {
    if (media.length > 0)
        return (
            <MediaTableWithItems
                domainId={domainId}
                media={media}
                update={update}
                disabled={disabled}
                tableStyle={tableStyle}
                openMediaPlayerDialog={openMediaPlayerDialog}
                getErrorsForIndex={getErrorsForIndex}
            />
        );

    return <NoMedia update={update} disabled={disabled} />;
};

export default MediaTable;
